/**
 * Centralized environment configuration
 * Single source of truth for environment-dependent behavior
 */

/**
 * Check if running in development mode
 * Used for auth bypass, verbose logging, and dev-only features
 */
export const isDevelopment = import.meta.env.DEV || import.meta.env.MODE === 'development';

/**
 * Check if running in production mode
 */
export const isProduction = import.meta.env.PROD || import.meta.env.MODE === 'production';

/**
 * Get environment mode string
 */
export const environment = import.meta.env.MODE || 'development';

/**
 * Check if running on a local development hostname
 * Adds runtime verification that we're truly in a dev environment
 */
const isLocalHostname = (): boolean => {
  if (typeof window === 'undefined') return false;
  const { hostname } = window.location;
  return (
    hostname === 'localhost' ||
    hostname === '127.0.0.1' ||
    hostname === '[::1]' || // IPv6 localhost
    hostname.endsWith('.local') ||
    hostname.startsWith('192.168.') || // Local network
    hostname.startsWith('10.') || // Local network
    hostname.startsWith('172.16.') // Local network
  );
};

/**
 * Development auth bypass flag
 * When true, ProtectedRoute components skip authentication checks
 *
 * SECURITY: Requires BOTH build-time dev mode AND local hostname
 * This prevents misconfigured production builds from bypassing auth
 */
export const DEV_AUTH_BYPASS = isDevelopment && isLocalHostname();

/**
 * Enable verbose logging in development
 */
export const VERBOSE_LOGGING = isDevelopment;

/**
 * API base URL (defaults to same origin)
 */
export const API_BASE_URL = import.meta.env.VITE_API_URL || '';

/**
 * Lawful Notice Generator public launch gate.
 * When true, the subsystem is visible to all users with backend access.
 * When false (default), only admin/instructor see the nav entry and can reach routes.
 * Flip to true in Railway env at launch.
 */
export const LAWFUL_DOCS_PUBLIC = import.meta.env.VITE_LAWFUL_DOCS_PUBLIC === 'true';
